"use client";

import { c as _c } from "react/compiler-runtime";
import { getNextGenIconUrl } from "@/components/nextGen/collections/nextgenToken/NextGenTokenImage";
import { normalizeNextgenTokenID } from "@/components/nextGen/nextgen_helpers";
import type { NFTLite } from "@/components/user/settings/UserSettingsImgSelectMeme";
import EtherscanIcon from "@/components/user/utils/icons/EtherscanIcon";
import CommonTimeAgo from "@/components/utils/CommonTimeAgo";
import { GRADIENT_CONTRACT, MANIFOLD, MEMELAB_CONTRACT, MEMES_CONTRACT, NULL_ADDRESS, NULL_DEAD_ADDRESS } from "@/constants/constants";
import type { NextGenCollection } from "@/entities/INextgen";
import type { Transaction } from "@/entities/ITransaction";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { areEqualAddresses, isGradientsContract, isMemeLabContract, isMemesContract, isNextgenContract } from "@/helpers/Helpers";
import Link from "next/link";
import { useEffect, useState } from "react";
import UserPageStatsActivityWalletTableRowGas from "./UserPageStatsActivityWalletTableRowGas";
import UserPageStatsActivityWalletTableRowIcon from "./UserPageStatsActivityWalletTableRowIcon";
import UserPageStatsActivityWalletTableRowMainAddress from "./UserPageStatsActivityWalletTableRowMainAddress";
import UserPageStatsActivityWalletTableRowRoyalties from "./UserPageStatsActivityWalletTableRowRoyalties";
import UserPageStatsActivityWalletTableRowSecondAddress from "./UserPageStatsActivityWalletTableRowSecondAddress";
export enum TransactionType {
  AIRDROPPED = "AIRDROPPED",
  RECEIVED_AIRDROP = "RECEIVED_AIRDROP",
  SEIZED = "SEIZED",
  SEIZED_TO = "SEIZED_TO",
  SALE = "SALE",
  PURCHASE = "PURCHASE",
  TRANSFER_IN = "TRANSFER_IN",
  TRANSFER_OUT = "TRANSFER_OUT",
  BURNED = "BURNED",
  RECEIVED_BURN = "RECEIVED_BURN",
}
const TYPE_TP_ACTION: Record<TransactionType, string> = {
  [TransactionType.RECEIVED_AIRDROP]: "received airdrop",
  [TransactionType.SEIZED]: "seized",
  [TransactionType.SEIZED_TO]: "seized",
  [TransactionType.SALE]: "sold",
  [TransactionType.PURCHASE]: "purchased",
  [TransactionType.TRANSFER_IN]: "received",
  [TransactionType.TRANSFER_OUT]: "transferred",
  [TransactionType.BURNED]: "burned",
  [TransactionType.RECEIVED_BURN]: "received burn",
  [TransactionType.AIRDROPPED]: "airdropped"
};
const NULL_AND_DEAD_ADDRESSES = [NULL_ADDRESS, NULL_DEAD_ADDRESS].map(w => w.toLowerCase());
const MINTING_ADDRESSES = [NULL_ADDRESS, MANIFOLD].map(w => w.toLowerCase());
export default function UserPageStatsActivityWalletTableRow(t0) {
  const $ = _c(120);
  const {
    transaction,
    profile,
    memes,
    memeLab,
    nextgenCollections
  } = t0;
  let t1;
  if ($[0] !== transaction.from_address || $[1] !== transaction.royalties || $[2] !== transaction.value) {
    t1 = () => {
      if (!transaction.value) {
        return false;
      }
      if (areEqualAddresses(transaction.from_address, NULL_ADDRESS)) {
        return false;
      }
      if (areEqualAddresses(transaction.from_address, NULL_DEAD_ADDRESS)) {
        return false;
      }
      if (areEqualAddresses(transaction.from_address, MANIFOLD)) {
        return false;
      }
      if (!transaction.royalties) {
        return false;
      }
      return true;
    };
    $[0] = transaction.from_address;
    $[1] = transaction.royalties;
    $[2] = transaction.value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getShowRoyalties = t1;
  const showRoyalties = getShowRoyalties();
  let t2;
  if ($[4] !== profile.wallets) {
    t2 = profile.wallets?.map(_temp) ?? [];
    $[4] = profile.wallets;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [consolidatedAddresses, setConsolidatedAddresses] = useState(t2);
  let t3;
  if ($[6] !== profile.wallets) {
    t3 = () => setConsolidatedAddresses(profile.wallets?.map(_temp2) ?? []);
    $[6] = profile.wallets;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== profile) {
    t4 = [profile];
    $[8] = profile;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  const includingAddresses = _temp3;
  let t5;
  if ($[10] !== profile.wallets) {
    t5 = () => profile.wallets?.some(w_1 => includingAddresses({
      address: w_1.wallet,
      addresses: NULL_AND_DEAD_ADDRESSES
    })) ? TransactionType.AIRDROPPED : TransactionType.RECEIVED_AIRDROP;
    $[10] = profile.wallets;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const getAirDropType = t5;
  let t6;
  if ($[12] !== profile.wallets) {
    t6 = () => profile.wallets?.some(w_2 => includingAddresses({
      address: w_2.wallet,
      addresses: MINTING_ADDRESSES
    })) ? TransactionType.SEIZED_TO : TransactionType.SEIZED;
    $[12] = profile.wallets;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const getMintingType = t6;
  let t7;
  if ($[14] !== profile.wallets) {
    t7 = () => profile.wallets?.some(w_3 => includingAddresses({
      address: w_3.wallet,
      addresses: NULL_AND_DEAD_ADDRESSES
    })) ? TransactionType.RECEIVED_BURN : TransactionType.BURNED;
    $[14] = profile.wallets;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const getBurnType = t7;
  let t8;
  if ($[16] !== consolidatedAddresses || $[17] !== getAirDropType || $[18] !== getBurnType || $[19] !== getMintingType || $[20] !== transaction.from_address || $[21] !== transaction.to_address || $[22] !== transaction.value) {
    const getType = () => {
      if (areEqualAddresses(NULL_ADDRESS, transaction.from_address) && !transaction.value) {
        return getAirDropType();
      }
      if (includingAddresses({
        address: transaction.from_address,
        addresses: MINTING_ADDRESSES
      })) {
        return getMintingType();
      }
      if (includingAddresses({
        address: transaction.to_address,
        addresses: NULL_AND_DEAD_ADDRESSES
      })) {
        return getBurnType();
      }
      if (transaction.value && consolidatedAddresses.includes(transaction.to_address.toLowerCase())) {
        return TransactionType.PURCHASE;
      }
      if (transaction.value && consolidatedAddresses.includes(transaction.from_address.toLowerCase())) {
        return TransactionType.SALE;
      }
      if (consolidatedAddresses.includes(transaction.to_address.toLowerCase())) {
        return TransactionType.TRANSFER_IN;
      }
      return TransactionType.TRANSFER_OUT;
    };
    t8 = getType();
    $[16] = consolidatedAddresses;
    $[17] = getAirDropType;
    $[18] = getBurnType;
    $[19] = getMintingType;
    $[20] = transaction.from_address;
    $[21] = transaction.to_address;
    $[22] = transaction.value;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const type = t8;
  let nftLite;
  let showAnotherSide;
  let showValue;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t9;
  let value;
  if ($[24] !== memeLab || $[25] !== memes || $[26] !== nextgenCollections || $[27] !== profile || $[28] !== transaction || $[29] !== type) {
    nftLite = null;
    if (isMemesContract(transaction.contract)) {
      let t19;
      if ($[44] !== transaction.token_id) {
        t19 = m => m.id === transaction.token_id;
        $[44] = transaction.token_id;
        $[45] = t19;
      } else {
        t19 = $[45];
      }
      nftLite = memes.find(t19);
    } else {
      if (isMemeLabContract(transaction.contract)) {
        let t19;
        if ($[46] !== transaction.token_id) {
          t19 = m_0 => m_0.id === transaction.token_id;
          $[46] = transaction.token_id;
          $[47] = t19;
        } else {
          t19 = $[47];
        }
        nftLite = memeLab.find(t19);
      }
    }
    let t19;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = [TransactionType.PURCHASE, TransactionType.SALE, TransactionType.TRANSFER_IN, TransactionType.TRANSFER_OUT, TransactionType.RECEIVED_BURN, TransactionType.AIRDROPPED, TransactionType.SEIZED_TO];
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    showAnotherSide = t19.includes(type);
    value = transaction.value;
    let t20;
    if ($[49] !== transaction.contract || $[50] !== transaction.token_id) {
      t20 = () => {
        if (areEqualAddresses(MEMES_CONTRACT, transaction.contract)) {
          return `/the-memes/${transaction.token_id}`;
        }
        if (areEqualAddresses(GRADIENT_CONTRACT, transaction.contract)) {
          return `/6529-gradient/${transaction.token_id}`;
        }
        if (areEqualAddresses(MEMELAB_CONTRACT, transaction.contract)) {
          return `/meme-lab/${transaction.token_id}`;
        }
        if (isNextgenContract(transaction.contract)) {
          return `/nextgen/token/${transaction.token_id}`;
        }
        return "";
      };
      $[49] = transaction.contract;
      $[50] = transaction.token_id;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    const getPath = t20;
    const getLinkContent = () => {
      let name;
      if (isMemesContract(transaction.contract)) {
        name = `${nftLite?.name} (The Memes #${transaction.token_id})`;
      }
      if (isMemeLabContract(transaction.contract)) {
        name = `${nftLite?.name} (MemeLab #${transaction.token_id})`;
      }
      if (isNextgenContract(transaction.contract)) {
        const normalizedToken = normalizeNextgenTokenID(transaction.token_id);
        const collectionName = nextgenCollections.find(c => c.id === normalizedToken.collection_id)?.name ?? `NextGen #${normalizedToken.collection_id}`;
        name = `${collectionName} #${normalizedToken.token_id}`;
      }
      if (isGradientsContract(transaction.contract)) {
        name = `6529 Gradient #${transaction.token_id}`;
      }
      return name;
    };
    const getImageSrc = () => {
      let src = nftLite?.icon ?? "";
      if (isNextgenContract(transaction.contract)) {
        src = getNextGenIconUrl(transaction.token_id);
      }
      if (isGradientsContract(transaction.contract)) {
        src = `https://d3lqz0a4bldqgf.cloudfront.net/images/scaled_x60/${GRADIENT_CONTRACT.toLowerCase()}/${transaction.token_id}.WEBP`;
      }
      return src;
    };
    let t21;
    if ($[52] !== type || $[53] !== value) {
      t21 = () => {
        if (!value) {
          return false;
        }
        if (type === TransactionType.RECEIVED_BURN) {
          return false;
        }
        if (type === TransactionType.BURNED) {
          return false;
        }
        if (type === TransactionType.RECEIVED_AIRDROP) {
          return false;
        }
        if (type === TransactionType.AIRDROPPED) {
          return false;
        }
        return true;
      };
      $[52] = type;
      $[53] = value;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    showValue = t21;
    t18 = "tw-flex tw-items-center tw-justify-between";
    t17 = "tw-flex-1 tw-py-2.5";
    t11 = "tw-inline-flex tw-items-center tw-gap-x-1";
    if ($[55] !== type) {
      t12 = <UserPageStatsActivityWalletTableRowIcon type={type} />;
      $[55] = type;
      $[56] = t12;
    } else {
      t12 = $[56];
    }
    if ($[57] !== profile || $[58] !== transaction || $[59] !== type) {
      t13 = <UserPageStatsActivityWalletTableRowMainAddress transaction={transaction} type={type} profile={profile} />;
      $[57] = profile;
      $[58] = transaction;
      $[59] = type;
      $[60] = t13;
    } else {
      t13 = $[60];
    }
    const t22 = TYPE_TP_ACTION[type];
    if ($[61] !== t22) {
      t14 = <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-iron-400 sm:tw-text-base">{t22}</span>;
      $[61] = t22;
      $[62] = t14;
    } else {
      t14 = $[62];
    }
    if ($[63] !== transaction.token_count) {
      t15 = transaction.token_count > 1 && <span className="tw-whitespace-nowrap tw-text-base tw-font-medium tw-text-iron-300">x{transaction.token_count}</span>;
      $[63] = transaction.token_count;
      $[64] = t15;
    } else {
      t15 = $[64];
    }
    const t23 = getPath();
    let t24;
    if ($[65] !== getLinkContent) {
      t24 = getLinkContent();
      $[65] = getLinkContent;
      $[66] = t24;
    } else {
      t24 = $[66];
    }
    if ($[67] !== t23 || $[68] !== t24) {
      t16 = <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium sm:tw-text-base"><Link className="tw-text-iron-100 tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-400" href={t23}>{t24}</Link></span>;
      $[67] = t23;
      $[68] = t24;
      $[69] = t16;
    } else {
      t16 = $[69];
    }
    t9 = "tw-mx-0.5 tw-h-auto tw-max-h-10 tw-w-auto tw-min-w-10 tw-flex-shrink-0 tw-rounded-sm tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-white/30";
    t10 = getImageSrc();
    $[24] = memeLab;
    $[25] = memes;
    $[26] = nextgenCollections;
    $[27] = profile;
    $[28] = transaction;
    $[29] = type;
    $[30] = nftLite;
    $[31] = showAnotherSide;
    $[32] = showValue;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t9;
    $[43] = value;
  } else {
    nftLite = $[30];
    showAnotherSide = $[31];
    showValue = $[32];
    t10 = $[33];
    t11 = $[34];
    t12 = $[35];
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
    t16 = $[39];
    t17 = $[40];
    t18 = $[41];
    t9 = $[42];
    value = $[43];
  }
  const t19 = nftLite?.name ?? "";
  let t20;
  if ($[70] !== t10 || $[71] !== t19 || $[72] !== t9) {
    t20 = <img className={t9} src={t10} alt={t19} />;
    $[70] = t10;
    $[71] = t19;
    $[72] = t9;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== showAnotherSide || $[75] !== transaction || $[76] !== type) {
    t21 = showAnotherSide && <UserPageStatsActivityWalletTableRowSecondAddress type={type} transaction={transaction} />;
    $[74] = showAnotherSide;
    $[75] = transaction;
    $[76] = type;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== t21) {
    t22 = <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-iron-100 sm:tw-text-base">{t21}</span>;
    $[78] = t21;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  let t23;
  if ($[80] !== showValue || $[81] !== value) {
    t23 = showValue() && <span className="tw-inline-flex tw-items-center tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-iron-400 sm:tw-text-base">for{" "}<span className="tw-ml-0.5 tw-inline-flex tw-items-center"><svg className="tw-h-5 tw-w-5" aria-hidden="true" aria-label="ethereum" enableBackground="new 0 0 1920 1920" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg"><path d="m959.8 80.7-539.7 895.6 539.7-245.3z" fill="#8a92b2" /><path d="m959.8 731-539.7 245.3 539.7 319.1z" fill="#62688f" /><path d="m1499.6 976.3-539.8-895.6v650.3z" fill="#62688f" /><path d="m959.8 1295.4 539.8-319.1-539.8-245.3z" fill="#454a75" /><path d="m420.1 1078.7 539.7 760.6v-441.7z" fill="#8a92b2" /><path d="m959.8 1397.6v441.7l540.1-760.6z" fill="#62688f" /></svg><span className="tw-whitespace-nowrap tw-text-iron-100">{value}</span></span></span>;
    $[80] = showValue;
    $[81] = value;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] !== t11 || $[84] !== t12 || $[85] !== t13 || $[86] !== t14 || $[87] !== t15 || $[88] !== t16 || $[89] !== t20 || $[90] !== t22 || $[91] !== t23) {
    t24 = <span className={t11}>{t12}{t13}{t14}{t15}{t16}{t20}{t22}{t23}</span>;
    $[83] = t11;
    $[84] = t12;
    $[85] = t13;
    $[86] = t14;
    $[87] = t15;
    $[88] = t16;
    $[89] = t20;
    $[90] = t22;
    $[91] = t23;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  let t25;
  if ($[93] !== t17 || $[94] !== t24) {
    t25 = <td className={t17}>{t24}</td>;
    $[93] = t17;
    $[94] = t24;
    $[95] = t25;
  } else {
    t25 = $[95];
  }
  let t26;
  if ($[96] !== showRoyalties || $[97] !== transaction.royalties || $[98] !== transaction.value) {
    t26 = showRoyalties && <UserPageStatsActivityWalletTableRowRoyalties transactionValue={transaction.value} royalties={transaction.royalties} />;
    $[96] = showRoyalties;
    $[97] = transaction.royalties;
    $[98] = transaction.value;
    $[99] = t26;
  } else {
    t26 = $[99];
  }
  let t27;
  if ($[100] !== transaction.gas || $[101] !== transaction.gas_gwei || $[102] !== transaction.gas_price_gwei) {
    t27 = <UserPageStatsActivityWalletTableRowGas gas={transaction.gas} gasGwei={transaction.gas_gwei} gasPriceGwei={transaction.gas_price_gwei} />;
    $[100] = transaction.gas;
    $[101] = transaction.gas_gwei;
    $[102] = transaction.gas_price_gwei;
    $[103] = t27;
  } else {
    t27 = $[103];
  }
  const t28 = `https://etherscan.io/tx/${transaction.transaction}`;
  let t29;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="tw-h-6 tw-w-6 tw-flex-shrink-0 sm:tw-h-5 sm:tw-w-5"><EtherscanIcon /></span>;
    $[104] = t29;
  } else {
    t29 = $[104];
  }
  let t30;
  if ($[105] !== t28) {
    t30 = <a href={t28} target="_blank" title="Go to etherscan" aria-label="Go to etherscan" rel="noopener noreferrer" className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-transition tw-duration-300 tw-ease-out hover:tw-scale-110 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400">{t29}</a>;
    $[105] = t28;
    $[106] = t30;
  } else {
    t30 = $[106];
  }
  let t31;
  if ($[107] !== t26 || $[108] !== t27 || $[109] !== t30) {
    t31 = <td className="tw-h-full tw-px-6 tw-py-2.5 tw-text-right sm:tw-px-4"><span className="tw-flex tw-items-center tw-justify-end">{t26}{t27}{t30}</span></td>;
    $[107] = t26;
    $[108] = t27;
    $[109] = t30;
    $[110] = t31;
  } else {
    t31 = $[110];
  }
  let t32;
  if ($[111] !== transaction.transaction_date) {
    t32 = new Date(transaction.transaction_date).getTime();
    $[111] = transaction.transaction_date;
    $[112] = t32;
  } else {
    t32 = $[112];
  }
  let t33;
  if ($[113] !== t32) {
    t33 = <td className="tw-w-24 tw-py-2.5 tw-text-right sm:tw-w-32"><CommonTimeAgo timestamp={t32} /></td>;
    $[113] = t32;
    $[114] = t33;
  } else {
    t33 = $[114];
  }
  let t34;
  if ($[115] !== t18 || $[116] !== t25 || $[117] !== t31 || $[118] !== t33) {
    t34 = <tr className={t18}>{t25}{t31}{t33}</tr>;
    $[115] = t18;
    $[116] = t25;
    $[117] = t31;
    $[118] = t33;
    $[119] = t34;
  } else {
    t34 = $[119];
  }
  return t34;
}
function _temp3(t0) {
  const {
    address,
    addresses
  } = t0;
  return addresses.some(a => areEqualAddresses(a.toLowerCase(), address.toLowerCase()));
}
function _temp2(w_0) {
  return w_0.wallet.toLowerCase();
}
function _temp(w) {
  return w.wallet.toLowerCase();
}